import moment from 'moment';
import { Select, Button } from 'semantic-ui-react';
import { useMemo, useCallback } from 'react';
import { SelectedAccount } from 'types/api/Account';
import { useGetAccountAssessmentTemplates } from 'actions/envScales/accountAssessmentTemplates';
import {
  getObserverOptions,
  getTemplatesOptions,
  buildHierarchy
} from './utils';
import i18n from 'helpers/i18n';
import messages from './messages';
import HierarchyDropdown from 'components/Reports/Observation/HierarchyDropdown';
import { useGetScopedAccountHierarchy } from 'actions/accounts';
import { useGetAllObservers } from 'actions/observers';
import { classMixedEditionAgeLevels } from 'constants/ageLevels';
import DateRangePicker from 'components/Reports/Observation/DateRangePicker';
import useTimestamp from 'hooks/useTimestamp';

interface FilterProps {
  account?: SelectedAccount;
  state: {
    assessment_template_id?: string;
    age_level?: string;
    observer_guids?: string[];
    start_date?: string;
    end_date?: string;
    node_ids?: number[];
    informal?: boolean;
  };
  dates: {
    start_date: Date | null | undefined;
    end_date: Date | null | undefined;
  };
  isObserver: boolean;
  onFilterStatus: (filter: any) => void;
  onDatesChange: (
    startDate: Date | null | undefined,
    endDate: Date | null | undefined
  ) => void;
  onClear: () => void;
}

const formalityOptions = [
  { key: 'informal', value: true, text: i18n.ft(messages.informal) },
  { key: 'formal', value: false, text: i18n.ft(messages.formal) }
];

function Filters({
  account,
  state,
  onFilterStatus,
  onClear,
  dates,
  isObserver,
  onDatesChange
}: FilterProps) {
  const accountGuid = account === undefined ? '' : account.guid;
  const currentAccountId = account === undefined ? 0 : account.id;
  const { data: templatesData } = useGetAccountAssessmentTemplates(accountGuid);
  const { data: hierarchyData, isSuccess: hasHierarchyData } =
    useGetScopedAccountHierarchy(currentAccountId);
  const { data: observersData } = useGetAllObservers(currentAccountId);
  const [timestamp, refreshTimestamp] = useTimestamp();

  const ageLevelOptions = classMixedEditionAgeLevels;
  const tempOptions = getTemplatesOptions(
    templatesData?.account_assessment_templates || []
  );

  const selectedAgeLevel = ageLevelOptions.find(
    (option: any) => option.value === state.age_level
  );
  const selectedTemplate = tempOptions.find((option: any) => {
    return (
      option.value === state.assessment_template_id ||
      option.id === Number(state.assessment_template_id)
    );
  });

  let filteredTempOptions = tempOptions;
  if (selectedAgeLevel) {
    if (selectedAgeLevel.class_version === 2) {
      filteredTempOptions = tempOptions.filter(
        (option: any) => option.classVersion === 2
      );
    } else if (selectedAgeLevel.class_version === 1) {
      filteredTempOptions = tempOptions.filter(
        (option: any) => option.classVersion === 1
      );
    }
  }

  let filteredAgeLevelOptions = ageLevelOptions;
  if (selectedTemplate && selectedTemplate.classVersion === 2) {
    filteredAgeLevelOptions = ageLevelOptions.filter(
      (option: any) => option.class_version === 2
    );
  } else if (selectedTemplate && selectedTemplate.classVersion === 1) {
    filteredAgeLevelOptions = ageLevelOptions.filter(
      (option: any) => option.class_version === 1
    );
  }

  const observersOptions = getObserverOptions(observersData);

  /**
   * Need to use `useCallback` and `useMemo` because of a bug with react-dropdown-tree-select
   * that would cause the entire list of selected options to be lost if any of the props change.
   */
  const hierarchy = useMemo(() => {
    const hierarchyNodes = Array.isArray(hierarchyData?.hierarchy)
      ? hierarchyData?.hierarchy
      : hierarchyData?.hierarchy?.children || [];

    return hasHierarchyData
      ? buildHierarchy(hierarchyNodes, state.node_ids)
      : [];
    // Disabling just because of state.node_ids (only needed for the first initial state).
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [hasHierarchyData, hierarchyData]);

  const handleChange = useCallback(
    (_: any, tags: any) => {
      const nodeIds = tags.map((t: any) => Number(t.value));
      onFilterStatus({ node_ids: nodeIds as string[] });
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    []
  );

  function clear() {
    onClear();
    refreshTimestamp();
  }

  return (
    <div className="mt-3 flex flex-col gap-2 lg:grid lg:[grid-template-columns:1fr_1fr] lg:gap-x-8 lg:gap-y-4 2xl:gap-x-12 2xl:gap-y-4">
      <div className="mt-4 grid gap-4 md:mt-0 md:[grid-template-columns:1fr_1fr]">
        <Select
          name="type"
          options={filteredTempOptions}
          value={state.assessment_template_id}
          placeholder={i18n.ft(messages.classVersion)}
          onChange={(_, data) =>
            onFilterStatus({ assessment_template_id: data.value as string })
          }
        />
        <Select
          name="age_level"
          options={filteredAgeLevelOptions}
          value={state.age_level}
          placeholder={i18n.ft(messages.ageLevel)}
          onChange={(_, data) =>
            onFilterStatus({ age_level: data.value as string })
          }
        />
        <Select
          name="informal"
          options={formalityOptions}
          value={state.informal}
          placeholder={i18n.ft(messages.formality)}
          onChange={(_, data) => onFilterStatus({ informal: data.value })}
        />
      </div>

      <div className="rounded-md">
        <DateRangePicker
          startDate={dates.start_date}
          endDate={dates.end_date}
          onChange={([startDate, endDate]) => {
            onDatesChange(startDate, endDate);
            onFilterStatus({
              start_date: startDate
                ? moment(startDate).format('YYYY-MM-DD')
                : null
            });
            onFilterStatus({
              end_date: endDate ? moment(endDate).format('YYYY-MM-DD') : null
            });
          }}
        />
      </div>

      <div className="mt-4 grid gap-4 md:mt-0 md:grid-cols-1">
        {!isObserver && (
          <Select
            name="observers"
            options={observersOptions}
            value={state.observer_guids}
            placeholder={i18n.ft(messages.observers)}
            onChange={(_, data) =>
              onFilterStatus({ observer_guids: data.value as string[] })
            }
            multiple
          />
        )}
        <HierarchyDropdown
          data={hierarchy}
          onChange={handleChange}
          placeholder={i18n.ft(messages.hierarchy)}
          key={timestamp}
        />
      </div>
      <div className="mt-4 grid gap-4 md:mt-0 md:grid-cols-1 md:grid-rows-2">
        <Button onClick={() => clear()}>{i18n.ft(messages.clear)}</Button>
        <div></div>
      </div>
    </div>
  );
}

export default Filters;
